/**
 * CADINU subgraphs on BSC: which backend serves each one. No secrets in this file (the Graph API key is read
 * server-side only, see server/subgraph/upstream.ts).
 *
 * Our three subgraphs live in Bez's Subgraph Studio account 101091 and were published to The Graph Network on
 * 2026-09-30. They replace the old Studio account 50708 (not confirmed as Bez's; Studio stops serving BSC subgraphs
 * that exist only in Studio on 2026-10-08).
 * The lottery subgraph (`cadinu-lottery`, source apps-dex/subgraphs/cadinu-lottery in the audit workspace) goes through the
 * same proxy. Until Bez deploys it to Studio 101091 it is read from the old 50708 URL (`legacy`, the default) — the same
 * data the page read before, now server side. Then: SUBGRAPH_LOTTERY_SOURCE=studio; after publishing to the network:
 * SUBGRAPH_LOTTERY_SOURCE=gateway + SUBGRAPH_LOTTERY_DEPLOYMENT=<Qm…> (env + restart, no rebuild).
 *
 * One switch per subgraph, read from the server environment at run time (no rebuild, `pm2 restart --update-env`):
 *   SUBGRAPH_EXCHANGE_SOURCE     = gateway | studio | off     (default studio)
 *   SUBGRAPH_EXCHANGE_V3_SOURCE  = gateway | studio | off     (default studio)
 *   SUBGRAPH_BLOCKS_SOURCE       = gateway | studio | off     (default off: still syncing, ~25 % on 2026-09-30)
 *   SUBGRAPH_LOTTERY_SOURCE      = gateway | studio | legacy | off (default legacy = Studio 50708, dead after 2026-10-08;
 *                                  gateway needs SUBGRAPH_LOTTERY_DEPLOYMENT, there is no published id yet)
 *   SUBGRAPH_<NAME>_GATEWAY_ID   = what the gateway is asked for, in either form, told apart by the prefix (2026-10-08):
 *                                  `Qm…` = a deployment id (one fixed version, gateway path deployments/id/<Qm…>);
 *                                  anything else base58 = the Explorer's Subgraph ID (follows the version published as current,
 *                                  gateway path subgraphs/id/<id>). exchange-v3 v1.0.1 = QmQnUdw6sAMebjCZ4vEQHD3QuebddyM6hfvAyKWo59vpTC;
 *                                  its Subgraph ID (cadinu-exchange-v3) is 8gKLRJcKkdEst165rV3yodkskBNy3fBkPEcWTgp3dL6a.
 *   SUBGRAPH_<NAME>_DEPLOYMENT   = Qm… deployment id for the gateway, the older line; read when GATEWAY_ID is absent or malformed
 *                                  (default: the one published 2026-09-30)
 *   SUBGRAPH_EXCHANGE_V3_TOLERANT = 0 turns the indexing-error tolerance of exchange-v3 off (default on)
 *   SUBGRAPH_<NAME>_STUDIO_VERSION = v1.2.3 | latest   Studio version label the `studio` source reads (default per subgraph below).
 *                                  exchange-v3 is pinned to v1.0.0 (QmcX…z6M), the synced version: `version/latest` names the newest DEPLOYED version,
 *                                  so a fixed version deployed to the Studio could be served while it still syncs from its start block (2026-10-03). Move
 *                                  the label only after the new version has synced and passed the checks of apps-dex/subgraphs/README.md.
 *   SUBGRAPH_FALLBACK            = off  disables the automatic gateway -> studio fallback (default on)
 *   GRAPH_API_KEY                the Graph API key (gateway only; server side, sent as a Bearer header)
 *   GRAPH_GATEWAY_ORIGIN         optional Origin header for the gateway, for a key restricted to domains
 *
 *  - gateway: https://gateway.thegraph.com/api/deployments/id/<Qm…> or …/api/subgraphs/id/<Subgraph ID>, key required (Bearer header).
 *  - studio:  https://api.studio.thegraph.com/query/101091/<slug>/version/latest. Development endpoint of the Studio,
 *             rate limited; the Studio URLs of subgraphs published to the network may keep answering until 2026-10-31.
 *  - legacy:  the old Studio 50708 URL (`legacyUrl`), lottery only, kept until our own deployment answers.
 *  - off:     the subgraph is reported as unavailable (HTTP 503 from the proxy) — nothing is queried.
 *
 * Browsers never talk to The Graph for these: they call the same-origin proxy /api/subgraph/<name>
 * (server/subgraph/proxy.ts), which holds the key, caches, and applies the indexing-error tolerance.
 */
export type SubgraphName = 'exchange' | 'exchange-v3' | 'blocks' | 'lottery'
export type SubgraphSource = 'gateway' | 'studio' | 'legacy' | 'off'

export interface SubgraphDef {
  name: SubgraphName
  /** Studio account 101091 slug */
  studioSlug: string
  /** The Graph Network deployment id (published by Bez 2026-09-30); '' = not published yet (gateway needs the env override) */
  deploymentId: string
  /** a pre-switch query URL (old Studio account 50708), the `legacy` source; only where our own deployment is not live yet */
  legacyUrl?: string
  /** Studio version label the `studio` source reads: 'latest' (version/latest) or a deployed label such as 'v1.0.0' */
  studioVersion: string
  /** infix of the environment variables: SUBGRAPH_<ENV_KEY>_SOURCE */
  envKey: string
  defaultSource: SubgraphSource
  /**
   * The deployment reports non-fatal indexing errors (`_meta.hasIndexingErrors`). Entity queries then need
   * `subgraphError: allow`, `_meta` has to be asked on its own, and the network gateway refuses to answer them
   * ("bad indexers … no attestation: indexing_error") — so this one is served from the Studio until a fixed
   * version is published.
   */
  toleratesIndexingErrors: boolean
}

export const SUBGRAPHS: Record<SubgraphName, SubgraphDef> = {
  exchange: {
    name: 'exchange',
    studioSlug: 'cadinu-exchange',
    deploymentId: 'QmS9KxEWGHjpa5iCqVz8mMprKwG167UYM4mSBXjSgmq8fB',
    studioVersion: 'latest',
    envKey: 'EXCHANGE',
    defaultSource: 'studio',
    toleratesIndexingErrors: false,
  },
  'exchange-v3': {
    name: 'exchange-v3',
    studioSlug: 'cadinu-exchange-v3',
    deploymentId: 'QmcXkfCAcTJCiRnSNiRaUWeuHSBHr2wTaQgC5Ezmw4vz6M',
    studioVersion: 'v1.0.0',
    envKey: 'EXCHANGE_V3',
    defaultSource: 'studio',
    toleratesIndexingErrors: true,
  },
  blocks: {
    name: 'blocks',
    studioSlug: 'cadinu-blocks',
    deploymentId: 'QmNaVhunT2LpeQ5B4okijcYDebQrLftdQBr2yPMpmZw1hC',
    studioVersion: 'latest',
    envKey: 'BLOCKS',
    defaultSource: 'off',
    toleratesIndexingErrors: false,
  },
  lottery: {
    name: 'lottery',
    studioSlug: 'cadinu-lottery',
    deploymentId: '',
    legacyUrl: 'https://api.studio.thegraph.com/query/50708/lottery/version/latest',
    studioVersion: 'latest',
    envKey: 'LOTTERY',
    defaultSource: 'legacy',
    toleratesIndexingErrors: false,
  },
}

export const SUBGRAPH_NAMES = Object.keys(SUBGRAPHS) as SubgraphName[]
export const isSubgraphName = (value: unknown): value is SubgraphName =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(SUBGRAPHS, value)

export const STUDIO_ACCOUNT_BASE = 'https://api.studio.thegraph.com/query/101091'
export const DEFAULT_GATEWAY_BASE = 'https://gateway.thegraph.com/api'
export const PROXY_PATH = '/api/subgraph'
/** RFC 6761: never resolves — the endpoint of a subgraph that is switched off, so a stray client fails at once. */
export const OFF_URL = 'https://subgraph-switched-off.invalid'

export type Env = Record<string, string | undefined>

const SOURCES: SubgraphSource[] = ['gateway', 'studio', 'legacy', 'off']
/** an IPFS CIDv0: the deployment id of one subgraph version (46 chars, always `Qm`) */
const DEPLOYMENT_ID = /^Qm[1-9A-HJ-NP-Za-km-z]{44}$/
/** the Explorer's Subgraph ID: base58 of the subgraph's 32-byte id (43–44 chars; a margin either side, never `Qm`) */
const SUBGRAPH_ID = /^[1-9A-HJ-NP-Za-km-z]{40,50}$/

export type GatewayIdForm = 'deployment' | 'subgraph'
export interface GatewayId {
  form: GatewayIdForm
  id: string
}

/** The form of a gateway id by its prefix: `Qm…` is a deployment (one fixed version), other base58 is a Subgraph ID; anything else is no id. */
export const parseGatewayId = (raw: string | undefined): GatewayId | null => {
  const id = (raw ?? '').trim()
  if (DEPLOYMENT_ID.test(id)) return { form: 'deployment', id }
  if (SUBGRAPH_ID.test(id) && !id.startsWith('Qm')) return { form: 'subgraph', id }
  return null
}

export const sourceFor = (name: SubgraphName, env: Env): SubgraphSource => {
  const def = SUBGRAPHS[name]
  const raw = (env[`SUBGRAPH_${def.envKey}_SOURCE`] ?? '').trim().toLowerCase()
  if (!(SOURCES as string[]).includes(raw)) return def.defaultSource
  if (raw === 'legacy' && !def.legacyUrl) return def.defaultSource
  // gateway without any id (lottery before it is published) cannot answer: keep the default
  if (raw === 'gateway' && !gatewayIdFor(name, env)) return def.defaultSource
  return raw as SubgraphSource
}

export const deploymentFor = (name: SubgraphName, env: Env): string => {
  const raw = (env[`SUBGRAPH_${SUBGRAPHS[name].envKey}_DEPLOYMENT`] ?? '').trim()
  return DEPLOYMENT_ID.test(raw) ? raw : SUBGRAPHS[name].deploymentId
}

/**
 * What the gateway is asked for: SUBGRAPH_<NAME>_GATEWAY_ID in either form, else SUBGRAPH_<NAME>_DEPLOYMENT (Qm… only), else the
 * built-in deployment id; null when there is none (the gateway source is then not available for this subgraph).
 */
export const gatewayIdFor = (name: SubgraphName, env: Env): GatewayId | null => {
  const configured = parseGatewayId(env[`SUBGRAPH_${SUBGRAPHS[name].envKey}_GATEWAY_ID`])
  if (configured) return configured
  const deployment = deploymentFor(name, env)
  return deployment ? { form: 'deployment', id: deployment } : null
}

const STUDIO_VERSION = /^(latest|v\d{1,3}\.\d{1,3}\.\d{1,3})$/

export const studioVersionFor = (name: SubgraphName, env: Env): string => {
  const raw = (env[`SUBGRAPH_${SUBGRAPHS[name].envKey}_STUDIO_VERSION`] ?? '').trim()
  return STUDIO_VERSION.test(raw) ? raw : SUBGRAPHS[name].studioVersion
}

export const toleranceFor = (name: SubgraphName, env: Env): boolean => {
  const def = SUBGRAPHS[name]
  if (!def.toleratesIndexingErrors) return false
  return (env[`SUBGRAPH_${def.envKey}_TOLERANT`] ?? '').trim() !== '0'
}

export const gatewayBase = (env: Env): string => (env.GRAPH_GATEWAY_URL || DEFAULT_GATEWAY_BASE).replace(/\/+$/, '')

/** The upstream URL of one source, without any key. */
export const upstreamUrl = (name: SubgraphName, source: SubgraphSource, env: Env): string | null => {
  if (source === 'gateway') {
    const gid = gatewayIdFor(name, env)
    return gid ? `${gatewayBase(env)}/${gid.form === 'subgraph' ? 'subgraphs' : 'deployments'}/id/${gid.id}` : null
  }
  if (source === 'studio') {
    const label = studioVersionFor(name, env)
    return `${STUDIO_ACCOUNT_BASE}/${SUBGRAPHS[name].studioSlug}/${label === 'latest' ? 'version/latest' : label}`
  }
  if (source === 'legacy') return SUBGRAPHS[name].legacyUrl ?? null
  return null
}

/** True in Node (SSR, API routes, build); false in browsers and web workers, where `process` is a shim. */
export const isServerRuntime = (): boolean =>
  typeof process !== 'undefined' && Boolean((process as { versions?: { node?: string } }).versions?.node)

export const proxyUrl = (name: SubgraphName) => `${PROXY_PATH}/${name}`

/**
 * The URL client code passes to a GraphQL client. Browser: the same-origin proxy. Server: the upstream endpoint of
 * the configured source (auth headers are added by utils/graphql.ts getGQLHeaders / server/subgraph).
 */
export const subgraphClientUrl = (
  name: SubgraphName,
  opts: { server?: boolean; env?: Env } = {},
): string => {
  if (!(opts.server ?? isServerRuntime())) return proxyUrl(name)
  const env = opts.env ?? (process.env as Env)
  return upstreamUrl(name, sourceFor(name, env), env) ?? `${OFF_URL}/${name}`
}
